import { Component, Input, OnInit, inject, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { ViewAsClientService } from '../../../core/services/view-as-client.service';
import { Client } from '../../../core/models/client.model';
import { DatePipe } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ClientFilters } from '../../../core/models/user-settings/client-filters.model';
import { UserSettings } from '../../../core/enums/user-settings.enum';
import { UserSettingsService } from '../../../core/services/user-settings.service';
import { BlankValueDashComponent } from '../../../shared/components/blank-value-dash/blank-value-dash.component';
import { PhoneNumberComponent } from '../../../shared/components/phone-number/phone-number.component';
import { JoinListPipe } from '../../../shared/pipes/join-list.pipe';
import { BlankValueDashPipe } from '../../../shared/pipes/blank-value-dash.pipe';
import { StateOptions } from '../../../core/options/states.opts';
import { BooleanToYesNoPipe } from '../../../shared/pipes/boolean-to-yes-no.pipe';

@Component({
  selector: 'app-client-list',
  templateUrl: './client-list.component.html',
  styleUrls: ['./client-list.component.scss'],
  imports: [RouterLink,
    RouterLinkActive,
    BlankValueDashComponent,
    PhoneNumberComponent,
    DatePipe,
    JoinListPipe,
    BlankValueDashPipe,
    BooleanToYesNoPipe,
    FormsModule,
    CommonModule,
    NgSelectModule]
})
export class ClientListComponent implements OnInit, OnDestroy {
  private filtersChanged$ = new Subject<void>();
  private suppressSave = true;
  private filtersChangedSubscription?: Subscription;

  private viewAsClientService = inject(ViewAsClientService);
  private userSettingsService = inject(UserSettingsService);

  @Input() clients: Client[];
  @Input() canManageClients: boolean;
  @Input() canManageClientConfigurations: boolean;

  readonly stateOptions = StateOptions;

  entityTypeOptions: string[] = [];
  assignedClientLeadOptions: string[] = [];
  filters: ClientFilters = new ClientFilters();

  ngOnInit() {
    this.buildOptions();
    this.loadFilters();
    this.startListening();
  }

  ngOnDestroy() {
    this.filtersChangedSubscription?.unsubscribe();
  }

  onFiltersChanged() {
    this.filtersChanged$.next();
  }

  get filteredClients(): Client[] {
    const f = this.filters;

    return (this.clients || []).filter(c => {
      if (f.name.length > 0) {
        const upperSearchString = f.name.toUpperCase();
        if (
          !c.name?.toUpperCase().includes(upperSearchString) &&
            !c.humanizedName?.toUpperCase().includes(upperSearchString)
        ) return false;
      }

      if (f.active !== 'any') {
        const active = c.status === 'active';
        if (f.active === 'yes' && !active) return false;
        if (f.active === 'no' && active) return false;
      }

      if (f.terminated !== 'any') {
        if (f.terminated === 'yes' && !c.terminated) return false;
        if (f.terminated === 'no' && c.terminated) return false;
      }

      if (f.fileProcessingEnabled !== 'any') {
        if (f.fileProcessingEnabled === 'yes' && !c.fileProcessingEnabled) return false;
        if (f.fileProcessingEnabled === 'no' && c.fileProcessingEnabled) return false;
      }

      // OR all entity types
      if (f.entityTypes?.length) {
        const clientTypes = c.entityTypes || [];
        const matchesSome = f.entityTypes.some(t => clientTypes.includes(t));
        if (!matchesSome) return false;
      }

      if (f.state) {
        if ((c.state || '').toUpperCase() !== f.state) return false;
      }

      if (f.assignedClientLeads?.length) {
        if (!(c.assignedUsers || []).some((u) => f.assignedClientLeads.includes(u.username))) return false;
      }

      return true;
    });
  }

  private buildOptions() {
    const types = new Set<string>();
    const leads = new Set<string>();

    for (const c of this.clients || []) {
      (c.entityTypes || []).forEach(t => t && types.add(t));
      (c.assignedUsers || []).forEach(u => u?.username && leads.add(u.username));
    }

    this.entityTypeOptions = Array.from(types).sort();
    this.assignedClientLeadOptions = Array.from(leads).sort();
  }

  private loadFilters() {
    const savedFilters: ClientFilters = this.userSettingsService.get<ClientFilters>(
      UserSettings.captureAdminClientFilters
    );
    if (savedFilters) {
      this.filters = Object.assign(new ClientFilters(), savedFilters);
    }
  }

  private saveFilters() {
    this.userSettingsService.save<ClientFilters>(UserSettings.captureAdminClientFilters, this.filters);
  }

  private startListening() {
    this.filtersChangedSubscription = this.filtersChanged$
      .pipe(debounceTime(300))
      .subscribe(() => {
        if (!this.suppressSave) this.saveFilters();
      });

    this.suppressSave = false;
  }

  clearFilters($event: Event) {
    $event.preventDefault();
    this.filters = new ClientFilters();
    this.onFiltersChanged();
  }

  clientActive(client: Client): boolean {
    return client?.status === 'active';
  }

  onViewPortalClick($event: Event, client: Client) {
    this.viewAsClientService.viewAsClient($event, client);
  }

  public assignedUsers(client: Client) {
    const assignedUsers = client.assignedUsers;
    if (assignedUsers && assignedUsers.length > 0) {
      return assignedUsers
        .map(u => u.username)
        .sort()
        .join(', ');
    } else {
      return 'no users assigned';
    }
  }
}
